<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { NButton, NCard, NBadge, NTag } from 'naive-ui'
import {
    LANGUAGES, getCurrentLanguage, setCurrentLanguage,
    getLocaleFromPreferred
} from '@/services/language'
import { dt } from '@/utils/i18n'
import providerDashboardImage from '@/assets/images/campaign-creators-pypeCEaJeZY-unsplash.jpg' // You'll need this
import verificationImage from '@/assets/images/marek-studzinski-iv-Iau6sLgw-unsplash.jpg'
import scheduleImage from '@/assets/images/medical-563427_640.jpg'
import analyticsImage from '@/assets/images/vitaly-gariev-8WYkI3cEZm8-unsplash.jpg'

const router = useRouter()
const { t, locale } = useI18n()

onMounted(() => {
    locale.value = getLocaleFromPreferred(getCurrentLanguage())
})

const handleProviderRegister = () => {
    // Pass provider context to register page
    router.push({
        name: 'register',
        query: { role: 'provider' }
    })
}

const handleProviderLogin = () => {
    // Pass provider context to login page
    router.push({
        name: 'login',
        query: { role: 'provider' }
    })
}
</script>

<template>
    <div class="provider-page">
        <!-- Hero Section for Providers -->
        <section class="provider-hero">
            <div class="container">
                <div class="hero-grid">
                    <div class="hero-content">
                        <h1 class="hero-title">{{ dt('providersHome.heroTitle') }}</h1>
                        <p class="hero-subtitle">{{ dt('providersHome.heroSubtitle') }}</p>

                        <div class="cta-buttons">
                            <NButton type="primary" size="large" class="cta-button-primary"
                                @click="handleProviderRegister">
                                {{ dt('providersHome.getStarted') }}
                            </NButton>
                            <NButton size="large" class="cta-button-secondary" @click="handleProviderLogin">
                                {{ dt('providersHome.providerLogin') }}
                            </NButton>
                        </div>

                        <div class="hero-stats">
                            <div class="stat-item">
                                <div class="stat-number">10,000+</div>
                                <div class="stat-label">{{ dt('providersHome.stats.patients') }}</div>
                            </div>
                            <div class="stat-item">
                                <div class="stat-number">12</div>
                                <div class="stat-label">{{ dt('providersHome.stats.languages') }}</div>
                            </div>
                            <div class="stat-item">
                                <div class="stat-number">100%</div>
                                <div class="stat-label">{{ dt('providersHome.stats.free') }}</div>
                            </div>
                        </div>
                    </div>

                    <div class="hero-image">
                        <img :src="providerDashboardImage" alt="Provider Dashboard" class="dashboard-preview" />
                    </div>
                </div>
            </div>
        </section>

        <!-- Why Join Section -->
        <section class="why-join">
            <div class="container">
                <h2 class="section-title">{{ dt('providersHome.whyJoinTitle') }}</h2>
                <div class="benefits-grid">
                    <div class="benefit-card">
                        <div class="benefit-icon">👥</div>
                        <h3>{{ dt('providersHome.benefits.reach.title') }}</h3>
                        <p>{{ dt('providersHome.benefits.reach.description') }}</p>
                    </div>
                    <div class="benefit-card">
                        <div class="benefit-icon">🗣️</div>
                        <h3>{{ dt('providersHome.benefits.multilingual.title') }}</h3>
                        <p>{{ dt('providersHome.benefits.multilingual.description') }}</p>
                    </div>
                    <div class="benefit-card">
                        <div class="benefit-icon">📊</div>
                        <h3>{{ dt('providersHome.benefits.profile.title') }}</h3>
                        <p>{{ dt('providersHome.benefits.profile.description') }}</p>
                    </div>
                    <div class="benefit-card">
                        <div class="benefit-icon">⭐</div>
                        <h3>{{ dt('providersHome.benefits.reputation.title') }}</h3>
                        <p>{{ dt('providersHome.benefits.reputation.description') }}</p>
                    </div>
                    <div class="benefit-card">
                        <div class="benefit-icon">📅</div>
                        <h3>{{ dt('providersHome.benefits.appointments.title') }}</h3>
                        <p>{{ dt('providersHome.benefits.appointments.description') }}</p>
                    </div>
                    <div class="benefit-card">
                        <div class="benefit-icon">✅</div>
                        <h3>{{ dt('providersHome.benefits.verification.title') }}</h3>
                        <p>{{ dt('providersHome.benefits.verification.description') }}</p>
                    </div>
                </div>
            </div>
        </section>

        <!-- Features for Providers -->
        <section class="provider-features">
            <div class="container">
                <h2 class="section-title">{{ dt('providersHome.featuresTitle') }}</h2>

                <!-- Feature 1: Profile Management -->
                <div class="feature-showcase">
                    <div class="feature-content">
                        <h3 class="feature-title">{{ dt('providersHome.features.profile.title') }}</h3>
                        <ul class="feature-list">
                            <li>✓ {{ dt('providersHome.features.profile.item1') }}</li>
                            <li>✓ {{ dt('providersHome.features.profile.item2') }}</li>
                            <li>✓ {{ dt('providersHome.features.profile.item3') }}</li>
                            <li>✓ {{ dt('providersHome.features.profile.item4') }}</li>
                        </ul>
                    </div>
                    <div class="feature-image">
                        <img :src="verificationImage" alt="Profile Management" />
                    </div>
                </div>

                <!-- Feature 2: Schedule Management -->
                <div class="feature-showcase reverse">
                    <div class="feature-content">
                        <h3 class="feature-title">{{ dt('providersHome.features.schedule.title') }}</h3>
                        <ul class="feature-list">
                            <li>✓ {{ dt('providersHome.features.schedule.item1') }}</li>
                            <li>✓ {{ dt('providersHome.features.schedule.item2') }}</li>
                            <li>✓ {{ dt('providersHome.features.schedule.item3') }}</li>
                            <li>✓ {{ dt('providersHome.features.schedule.item4') }}</li>
                        </ul>
                    </div>
                    <div class="feature-image">
                        <img :src="scheduleImage" alt="Schedule Management" />
                    </div>
                </div>

                <!-- Feature 3: Patient Engagement -->
                <div class="feature-showcase">
                    <div class="feature-content">
                        <h3 class="feature-title">{{ dt('providersHome.features.engagement.title') }}</h3>
                        <ul class="feature-list">
                            <li>✓ {{ dt('providersHome.features.engagement.item1') }}</li>
                            <li>✓ {{ dt('providersHome.features.engagement.item2') }}</li>
                            <li>✓ {{ dt('providersHome.features.engagement.item3') }}</li>
                            <li>✓ {{ dt('providersHome.features.engagement.item4') }}</li>
                        </ul>
                    </div>
                    <div class="feature-image">
                        <div class="coming-soon-badge">
                            <NBadge value="Coming Soon" type="warning" />
                        </div>
                        <img :src="analyticsImage" alt="Patient Engagement" />
                    </div>
                </div>
            </div>
        </section>

        <!-- How It Works for Providers -->
        <section class="how-it-works-providers">
            <div class="container">
                <h2 class="section-title">{{ dt('providersHome.howItWorksTitle') }}</h2>
                <div class="steps-grid">
                    <div class="step-card">
                        <div class="step-number">1</div>
                        <h4>{{ dt('providersHome.steps.register.title') }}</h4>
                        <p>{{ dt('providersHome.steps.register.description') }}</p>
                    </div>
                    <div class="step-card">
                        <div class="step-number">2</div>
                        <h4>{{ dt('providersHome.steps.profile.title') }}</h4>
                        <p>{{ dt('providersHome.steps.profile.description') }}</p>
                    </div>
                    <div class="step-card">
                        <div class="step-number">3</div>
                        <h4>{{ dt('providersHome.steps.verify.title') }}</h4>
                        <p>{{ dt('providersHome.steps.verify.description') }}</p>
                    </div>
                    <div class="step-card">
                        <div class="step-number">4</div>
                        <h4>{{ dt('providersHome.steps.connect.title') }}</h4>
                        <p>{{ dt('providersHome.steps.connect.description') }}</p>
                    </div>
                </div>
            </div>
        </section>

        <!-- Language Coverage - Same as Home -->
        <section class="languages">
            <div class="container">
                <h2 class="section-title">{{ dt('providersHome.languagesTitle') }}</h2>
                <p class="languages-subtitle">{{ dt('providersHome.languagesSubtitle') }}</p>
                <div class="language-tags">
                    <NTag v-for="lang in LANGUAGES" :key="lang.value" type="success" size="large" class="lang-tag">
                        {{ lang.label }}
                    </NTag>
                </div>
            </div>
        </section>

        <!-- Final CTA -->
        <section class="final-cta">
            <div class="container">
                <div class="cta-content">

                    <!-- Title - Good -->
                    <h2>{{ dt('providersHome.finalCta.title') }}</h2>

                    <!-- Subtitle - Good -->
                    <p>{{ dt('providersHome.finalCta.subtitle') }}</p>

                    <!-- Button Container - GOOD STRUCTURE -->
                    <div class="cta-buttons">
                        <NButton type="primary" size="large" class="cta-button-large" @click="handleProviderRegister">
                            {{ dt('providersHome.finalCta.button') }}
                        </NButton>
                    </div>

                    <!-- Trust note - Good -->
                    <p class="cta-note">{{ dt('providersHome.finalCta.note') }}</p>

                </div>
            </div>
        </section>

        <!-- For Patients Link -->
        <section class="for-patients-link">
            <div class="container">
                <p>{{ dt('providersHome.lookingForProvider') }}
                    <router-link to="/" class="patient-link">{{ dt('providersHome.goToPatientSite') }}</router-link>
                </p>
            </div>
        </section>
    </div>
</template>

<style scoped>
.provider-page {
    min-height: 100vh;
    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

.container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
}

/* Provider-themed Hero */
.provider-hero {
    background: linear-gradient(135deg, #16a085 0%, #1abc9c 100%);
    /* Teal gradient */
    color: white;
    padding: 5rem 0;
}

.hero-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 3rem;
    align-items: center;
}

.hero-content {
    text-align: left;
}

.hero-title {
    font-size: 3.5rem;
    margin-bottom: 1.5rem;
    font-weight: 700;
    line-height: 1.2;
}

.hero-subtitle {
    font-size: 1.4rem;
    margin-bottom: 2.5rem;
    opacity: 0.95;
    line-height: 1.6;
}

.cta-buttons {
    display: flex;
    gap: 1rem;
    margin-bottom: 3rem;
}

.cta-button-primary {
    background: #e67e22 !important;
    /* Orange accent */
    border: none;
    padding: 0 2.5rem;
    font-size: 1.1rem;
}

.cta-button-secondary {
    background: transparent;
    border: 2px solid white;
    color: white;
    padding: 0 2.5rem;
    font-size: 1.1rem;
}

.cta-button-secondary:hover {
    background: rgba(255, 255, 255, 0.1);
}

.hero-stats {
    display: flex;
    gap: 3rem;
}

.stat-item {
    text-align: center;
}

.stat-number {
    font-size: 2.5rem;
    font-weight: 700;
    margin-bottom: 0.5rem;
}

.stat-label {
    font-size: 0.9rem;
    opacity: 0.9;
}

.dashboard-preview {
    max-width: 100%;
    border-radius: 15px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

/* Why Join Section */
.why-join {
    padding: 5rem 0;
    background: #f8f9fa;
}

.section-title {
    text-align: center;
    font-size: 2.8rem;
    margin-bottom: 1rem;
    color: #16a085;
}

.benefits-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 2rem;
    margin-top: 3rem;
}

.benefit-card {
    background: white;
    padding: 2.5rem;
    border-radius: 12px;
    text-align: center;
    box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
    transition: all 0.3s ease;
    border-top: 4px solid #16a085;
}

.benefit-card:hover {
    transform: translateY(-8px);
    box-shadow: 0 10px 30px rgba(22, 160, 133, 0.2);
}

.benefit-icon {
    font-size: 3.5rem;
    margin-bottom: 1.5rem;
}

.benefit-card h3 {
    color: #2c3e50;
    margin-bottom: 1rem;
    font-size: 1.4rem;
}

.benefit-card p {
    color: #7f8c8d;
    line-height: 1.6;
}

/* Provider Features */
.provider-features {
    padding: 5rem 0;
}

.feature-showcase {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 4rem;
    align-items: center;
    margin: 4rem 0;
}

.feature-showcase.reverse {
    direction: rtl;
}

.feature-showcase.reverse .feature-content {
    direction: ltr;
}

.feature-title {
    font-size: 2rem;
    color: #16a085;
    margin-bottom: 1.5rem;
}

.feature-list {
    list-style: none;
    padding: 0;
}

.feature-list li {
    padding: 0.8rem 0;
    font-size: 1.1rem;
    color: #4a5568;
    border-bottom: 1px solid #e2e8f0;
}

.feature-image {
    position: relative;
}

.feature-image img {
    max-width: 100%;
    border-radius: 12px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
}

.coming-soon-badge {
    position: absolute;
    top: 20px;
    right: 20px;
    z-index: 10;
}

/* How It Works for Providers */
.how-it-works-providers {
    padding: 5rem 0;
    background: linear-gradient(135deg, #f6f9fc 0%, #edf2f7 100%);
}

.steps-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 2rem;
    margin-top: 3rem;
}

.step-card {
    background: white;
    text-align: center;
    padding: 2.5rem;
    border-radius: 12px;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
}

.step-number {
    width: 50px;
    height: 50px;
    background: #16a085;
    color: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 1.5rem;
    font-size: 1.5rem;
    font-weight: bold;
}

.step-card h4 {
    color: #2c3e50;
    margin-bottom: 1rem;
    font-size: 1.3rem;
}

.step-card p {
    color: #7f8c8d;
    line-height: 1.6;
}

/* Languages Section */
.languages {
    padding: 5rem 0;
    background: #f8f9fa;
}

.languages-subtitle {
    text-align: center;
    font-size: 1.2rem;
    color: #7f8c8d;
    margin-bottom: 2rem;
}

.language-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    justify-content: center;
    max-width: 800px;
    margin: 0 auto;
}

.lang-tag {
    font-size: 1.1rem;
}

/* Final CTA */
.final-cta {
  background: linear-gradient(135deg, #16a085 0%, #1abc9c 100%);
  padding: 80px 20px;
  text-align: center; /* ← Centers all text content */
}

.final-cta .container {
  max-width: 1200px;
  margin: 0 auto;
}

.final-cta .cta-content {
  max-width: 800px;
  margin: 0 auto;
}

/* Title */
.final-cta h2 {
  font-size: 2.5rem;
  font-weight: 700;
  color: white;
  margin-bottom: 1rem;
  text-align: center; /* ← Explicit center */
}

/* Subtitle */
.final-cta p {
  font-size: 1.25rem;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 2rem;
  text-align: center; /* ← Explicit center */
}

/* Button Container - THIS IS KEY */
.cta-buttons {
  display: flex;
  justify-content: center; /* ← Centers button horizontally */
  align-items: center;
  margin: 2rem 0;
}

/* Button Styling */
.cta-button-large {
  padding: 16px 48px !important;
  font-size: 18px !important;
  font-weight: 600 !important;
  background-color: #e67e22 !important; /* Orange for contrast */
  border: none !important;
  border-radius: 8px !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
  transition: all 0.3s ease !important;
  cursor: pointer;
}

.cta-button-large:hover {
  background-color: #d35400 !important;
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2) !important;
}

/* Trust Note */
.cta-note {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.8);
  margin-top: 1rem;
  text-align: center; /* ← Explicit center */
}

/* Responsive */
@media (max-width: 768px) {
  .final-cta {
    padding: 60px 20px;
  }
  
  .final-cta h2 {
    font-size: 2rem;
  }
  
  .final-cta p {
    font-size: 1.1rem;
  }
  
  .cta-button-large {
    padding: 14px 32px !important;
    font-size: 16px !important;
    width: 100%;
    max-width: 300px;
  }
}

/* For Patients Link */
.for-patients-link {
    padding: 2rem 0;
    text-align: center;
    background: #2c3e50;
    color: white;
}

.patient-link {
    color: #1abc9c;
    text-decoration: underline;
    font-weight: 600;
}

.patient-link:hover {
    color: #16a085;
}

/* Responsive */
@media (max-width: 768px) {

    .hero-grid,
    .feature-showcase,
    .feature-showcase.reverse {
        grid-template-columns: 1fr;
    }

    .hero-title {
        font-size: 2.2rem;
    }

    .cta-buttons {
        flex-direction: column;
    }

    .hero-stats {
        flex-direction: column;
        gap: 1.5rem;
    }

    .benefits-grid {
        grid-template-columns: 1fr;
    }
}
</style>